
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Thermometer, Droplets, Wifi, Battery, Calendar, Clock, AlertTriangle, CheckCircle, WifiOff, Plus, Building, Home } from 'lucide-react';
import MainLayout from '@/components/Layout/MainLayout';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { AddSensorDialog } from '@/components/AddSensorDialog';
import { supabase } from '@/integrations/supabase/client';
import { TemperatureSensorService } from '@/services/temperatureSensorService';

interface SensorData {
  id: string;
  name: string;
  temperature: number;
  humidity: number;
  batteryLevel: number;
  lastUpdate: string;
  status: 'online' | 'offline';
  location: string;
  buildingId?: string;
  apartmentId?: string;
  buildingName?: string;
  apartmentNumber?: string;
  alarmConfig: {
    tempMin: number;
    tempMax: number;
    humidityMin: number;
    humidityMax: number;
  };
}

const TemperatureHumidity = () => {
  const { isAdmin } = useAuth();
  const [addSensorOpen, setAddSensorOpen] = useState(false);
  const [sensors, setSensors] = useState<SensorData[]>([]);
  const [filteredSensors, setFilteredSensors] = useState<SensorData[]>([]);
  const [realTimeData, setRealTimeData] = useState<Map<string, any>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [buildings, setBuildings] = useState<any[]>([]);
  const [apartments, setApartments] = useState<any[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<string>('all');
  const [selectedApartment, setSelectedApartment] = useState<string>('all');

  useEffect(() => {
    fetchSensors();
    fetchBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuilding && selectedBuilding !== 'all') {
      fetchApartments(selectedBuilding);
    } else {
      setApartments([]);
      setSelectedApartment('all');
    }
  }, [selectedBuilding]);

  useEffect(() => {
    applyFilters();
  }, [sensors, selectedBuilding, selectedApartment]);

  useEffect(() => {
    // Initial fetch when sensors change
    if (sensors.length > 0) {
      fetchRealTimeData();
    }

    const interval = setInterval(() => {
      fetchRealTimeData();
    }, 15000); // Update every 15 seconds

    return () => clearInterval(interval);
  }, [sensors]);

  const fetchBuildings = async () => {
    try {
      const { data, error } = await supabase
        .from('buildings')
        .select('*')
        .order('name');
      
      if (data && !error) {
        setBuildings(data);
      }
    } catch (error) {
      console.error('Failed to fetch buildings:', error);
    }
  };

  const fetchApartments = async (buildingId: string) => {
    try {
      const { data, error } = await supabase
        .from('apartments')
        .select('*')
        .eq('building_id', buildingId)
        .order('apartment_number');
      
      if (data && !error) {
        setApartments(data);
      }
    } catch (error) {
      console.error('Failed to fetch apartments:', error);
    }
  };

  const applyFilters = () => {
    let filtered = sensors;

    if (selectedBuilding !== 'all') {
      filtered = filtered.filter(sensor => sensor.buildingId === selectedBuilding);

      if (selectedApartment !== 'all') {
        filtered = filtered.filter(sensor => sensor.apartmentId === selectedApartment);
      }
    }

    setFilteredSensors(filtered);
  };

  const fetchSensors = async () => {
    try {
      setIsLoading(true);
      const { data: sensorsData, error } = await supabase
        .from('temperature_sensors')
        .select(`
          *,
          buildings(id, name),
          apartments(id, apartment_number)
        `)
        .order('name');

      if (error) throw error;

      if (sensorsData) {
        const formattedSensors = sensorsData.map(sensor => {
          // Parse alarm config safely
          let alarmConfig;
          try {
            if (typeof sensor.alarm_config === 'object' && sensor.alarm_config !== null) {
              alarmConfig = sensor.alarm_config as any;
            } else {
              alarmConfig = {
                tempMin: 18,
                tempMax: 26,
                humidityMin: 30,
                humidityMax: 70
              };
            }
          } catch {
            alarmConfig = {
              tempMin: 18,
              tempMax: 26,
              humidityMin: 30,
              humidityMax: 70
            };
          }

          return {
            id: sensor.device_id,
            name: sensor.name,
            temperature: sensor.temperature || 0,
            humidity: sensor.humidity || 0,
            batteryLevel: sensor.battery_level || 0,
            lastUpdate: sensor.last_reading_at || new Date().toISOString(),
            status: sensor.is_online ? 'online' : 'offline' as 'online' | 'offline',
            location: sensor.location || 'Unknown',
            buildingId: sensor.building_id,
            apartmentId: sensor.apartment_id,
            buildingName: sensor.buildings?.name || 'Unknown Building',
            apartmentNumber: sensor.apartments?.apartment_number || 'No Apartment',
            alarmConfig: {
              tempMin: alarmConfig.temp_min || alarmConfig.tempMin || 18,
              tempMax: alarmConfig.temp_max || alarmConfig.tempMax || 26,
              humidityMin: alarmConfig.humidity_min || alarmConfig.humidityMin || 30,
              humidityMax: alarmConfig.humidity_max || alarmConfig.humidityMax || 70
            }
          };
        });
        setSensors(formattedSensors);
      }
    } catch (error) {
      console.error('Failed to fetch sensors:', error);
      toast({
        title: "Error",
        description: "Failed to fetch temperature sensors",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRealTimeData = async () => {
    if (sensors.length === 0) return;
    
    try {
      // Get all device IDs
      const deviceIds = sensors
        .map(sensor => sensor.id)
        .filter(id => id);

      if (deviceIds.length === 0) {
        return;
      }

      // Fetch real-time data for all sensors
      const realTimeResults = await TemperatureSensorService.fetchMultipleSensorsStatus(deviceIds);
      setRealTimeData(realTimeResults);
      
      // Update sensors state with real-time data
      setSensors(prevSensors => 
        prevSensors.map(sensor => {
          const realTime = realTimeResults.get(sensor.id);
          if (realTime) {
            return {
              ...sensor,
              temperature: realTime.temperature,
              humidity: realTime.humidity,
              batteryLevel: realTime.batteryLevel,
              status: realTime.isOnline ? 'online' : 'offline' as 'online' | 'offline',
              lastUpdate: new Date(realTime.lastUpdated).toISOString()
            };
          }
          return sensor;
        })
      );
    } catch (error) {
      console.error('Failed to fetch real-time sensor data:', error);
    }
  };

  const getTemperatureColor = (temp: number) => {
    if (temp < 15) return 'text-blue-600';
    if (temp < 20) return 'text-blue-400';
    if (temp < 25) return 'text-green-500';
    if (temp < 30) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getHumidityColor = (humidity: number) => {
    if (humidity < 30) return 'text-red-500';
    if (humidity < 40) return 'text-yellow-500';
    if (humidity < 70) return 'text-green-500';
    return 'text-blue-500';
  };

  const getBatteryColor = (level: number) => {
    if (level < 20) return 'text-red-500';
    if (level < 50) return 'text-yellow-500';
    return 'text-green-500';
  };

  const isAlarmTriggered = (sensor: SensorData) => {
    return (
      sensor.temperature < sensor.alarmConfig.tempMin ||
      sensor.temperature > sensor.alarmConfig.tempMax ||
      sensor.humidity < sensor.alarmConfig.humidityMin ||
      sensor.humidity > sensor.alarmConfig.humidityMax
    );
  };

  const refreshSensorData = async () => {
    await fetchRealTimeData();
    toast({
      title: "Sensor Data Refreshed",
      description: "Latest temperature and humidity data has been retrieved.",
    });
  };

  return (
    <MainLayout title="Temperature & Humidity" showBackButton={true}>
      <div className="p-4 space-y-6 max-w-full overflow-x-hidden">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white break-words">
              Environmental Monitoring
            </h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm break-words">
              Wi-Fi Temperature & Humidity Sensors
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={refreshSensorData}
              className="flex-1 sm:flex-none"
            >
              <Clock className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {isAdmin() && (
              <Button size="sm" className="flex-1 sm:flex-none" onClick={() => setAddSensorOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Sensor
              </Button>
            )}
          </div>
        </div>

        {/* Filter Section */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Filter by Building
                </label>
                <Select value={selectedBuilding} onValueChange={(val) => { setSelectedBuilding(val); setSelectedApartment('all'); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select building (All buildings)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Buildings</SelectItem>
                    {buildings.map((building) => (
                      <SelectItem key={building.id} value={building.id}>
                        {building.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  Filter by Apartment
                </label>
                <Select 
                  value={selectedApartment} 
                  onValueChange={setSelectedApartment}
                  disabled={selectedBuilding === 'all' || apartments.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={
                      selectedBuilding === 'all'
                        ? "Select building first" 
                        : apartments.length === 0 
                        ? "No apartments available"
                        : "Select apartment (All apartments)"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Apartments</SelectItem>
                    {apartments.map((apartment) => (
                      <SelectItem key={apartment.id} value={apartment.id}>
                        {apartment.apartment_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>


        {/* Sensors Grid */}
        {isLoading ? (
          <div className="text-center py-8">
            <div className="text-gray-500">Loading sensors...</div>
          </div>
        ) : filteredSensors.length === 0 && sensors.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500">No temperature sensors found.</div>
            {isAdmin() && (
              <Button 
                className="mt-4" 
                onClick={() => setAddSensorOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Sensor
              </Button>
            )}
          </div>
        ) : filteredSensors.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500">
              No sensors found for the selected filters.
              {selectedBuilding && <div className="text-sm mt-2">Try selecting a different building or apartment.</div>}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 xl:gap-6">
            {filteredSensors.map((sensor) => (
            <Card key={sensor.id} className="relative overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-lg flex items-center gap-2 min-w-0">
                      <span className="truncate">{sensor.name}</span>
                      {sensor.status === 'offline' && (
                        <Badge variant="destructive" className="text-xs flex-shrink-0">
                          <WifiOff className="w-3 h-3 mr-1" />
                          OFFLINE
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="space-y-1 mt-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-xs">{sensor.buildingName}</span>
                        {sensor.apartmentNumber && sensor.apartmentNumber !== 'No Apartment' && (
                          <>
                            <span className="text-xs">•</span>
                            <span className="truncate text-xs">{sensor.apartmentNumber}</span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="truncate text-xs">{sensor.location}</span>
                        <Badge 
                          variant={sensor.status === 'online' ? 'default' : 'destructive'}
                          className="text-xs flex-shrink-0"
                        >
                          <Wifi className="w-3 h-3 mr-1" />
                          {sensor.status}
                        </Badge>
                      </div>
                    </CardDescription>
                  </div>
                  {(isAlarmTriggered(sensor) || sensor.status === 'offline') && (
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Temperature & Humidity Display */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg min-w-0">
                    <Thermometer className={`w-6 h-6 mx-auto mb-2 ${sensor.status === 'offline' ? 'text-gray-400' : getTemperatureColor(sensor.temperature)}`} />
                    <div className={`text-base md:text-lg font-bold truncate ${sensor.status === 'offline' ? 'text-gray-400' : getTemperatureColor(sensor.temperature)}`}>
                      {sensor.status === 'offline' ? '--' : `${sensor.temperature}°C`}
                    </div>
                    <div className="text-xs text-gray-500 truncate">Temperature</div>
                  </div>
                  
                  <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg min-w-0">
                    <Droplets className={`w-6 h-6 mx-auto mb-2 ${sensor.status === 'offline' ? 'text-gray-400' : getHumidityColor(sensor.humidity)}`} />
                    <div className={`text-base md:text-lg font-bold truncate ${sensor.status === 'offline' ? 'text-gray-400' : getHumidityColor(sensor.humidity)}`}>
                      {sensor.status === 'offline' ? '--' : `${sensor.humidity}%`}
                    </div>
                    <div className="text-xs text-gray-500 truncate">Humidity</div>
                  </div>
                </div>

                <Separator />

                {/* Sensor Status */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-sm min-w-0">
                    <span className="text-gray-600 dark:text-gray-300 flex-shrink-0">Battery Level</span>
                    <div className="flex items-center gap-2 min-w-0">
                      <Battery className={`w-4 h-4 flex-shrink-0 ${sensor.status === 'offline' ? 'text-gray-400' : getBatteryColor(sensor.batteryLevel)}`} />
                      <span className={`flex-shrink-0 ${sensor.status === 'offline' ? 'text-gray-400' : getBatteryColor(sensor.batteryLevel)}`}>
                        {sensor.status === 'offline' ? '--' : `${sensor.batteryLevel}%`}
                      </span>
                      {sensor.status === 'offline' && sensor.batteryLevel < 20 && (
                        <span className="text-xs text-red-500 truncate">(Low when offline)</span>
                      )}
                    </div>
                  </div>
                  
                   <div className="flex justify-between items-center text-sm min-w-0">
                     <span className="text-gray-600 dark:text-gray-300 flex-shrink-0">Last Update</span>
                     <span className="text-gray-500 text-xs truncate">
                       {sensor.lastUpdate ? new Date(sensor.lastUpdate).toLocaleString() : 'Never'}
                     </span>
                   </div>
                  
                  <div className="flex justify-between items-center text-sm min-w-0">
                    <span className="text-gray-600 dark:text-gray-300 flex-shrink-0">Sensor ID</span>
                    <span className="text-gray-500 font-mono text-xs truncate">{sensor.id}</span>
                  </div>
                </div>

                {/* Alarm Status */}
                {isAdmin() && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between min-w-0">
                        <span className="text-sm font-medium flex-shrink-0">Alarm Status</span>
                        {sensor.status === 'offline' ? (
                          <Badge variant="destructive" className="text-xs flex-shrink-0">
                            <WifiOff className="w-3 h-3 mr-1" />
                            Offline
                          </Badge>
                        ) : isAlarmTriggered(sensor) ? (
                          <Badge variant="destructive" className="text-xs flex-shrink-0">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Triggered
                          </Badge>
                        ) : (
                          <Badge variant="default" className="text-xs flex-shrink-0">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Normal
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        <div className="break-words">Temp: {sensor.alarmConfig.tempMin}°C - {sensor.alarmConfig.tempMax}°C</div>
                        <div className="break-words">Humidity: {sensor.alarmConfig.humidityMin}% - {sensor.alarmConfig.humidityMax}%</div>
                        {sensor.status === 'offline' && (
                          <div className="text-red-500 break-words">Sensor offline - check connection and battery</div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
             ))}
           </div>
         )}

        {/* Add Sensor Dialog */}
        <AddSensorDialog 
          open={addSensorOpen} 
          onOpenChange={setAddSensorOpen}
          onSensorAdded={async () => {
            await fetchSensors();
            await fetchBuildings();
            toast({
              title: "Success",
              description: "Sensor added successfully",
            });
          }}
        />
      </div>
    </MainLayout>
  );
};

export default TemperatureHumidity;
